import { useNovelActsTree } from '@/stores/novelCoreStore'
import { useThesisWorkspaceStore } from '@/stores/thesisWorkspaceStore'
import { useThesisBriefStore } from '@/stores/thesisBriefStore'
import { cn } from '@/utils'
import { useParams } from '@/router/hooks'
import type { NovelActTreeType, NovelChapterSchema } from '#/index'
import { MBA_CHAPTER_CONTRACTS } from '@/lib/thesisFramework/chapterContracts'
import { gateStatusLabel, shortChapterTitle } from '@/lib/thesisFramework/gateLabels'

type Props = {
  onNavigate?: () => void
  className?: string
}

/** 稿面左侧目录：六章 + 小节（论文结构，非小说篇卷） */
export default function ThesisOutlinePanel({ onNavigate, className }: Props) {
  const { novelId } = useParams()
  const tree = useNovelActsTree()
  const activeChapterIndex = useThesisWorkspaceStore((s) => s.activeChapterIndex)
  const setActiveChapterIndex = useThesisWorkspaceStore((s) => s.setActiveChapterIndex)
  const setChapterDrafting = useThesisBriefStore((s) => s.setChapterDrafting)
  const brief = useThesisBriefStore((s) =>
    novelId ? s.byNovelId[novelId] || s.getBrief(novelId) : null,
  )

  const focusScene = (sceneId: string, chapterNo?: number) => {
    if (chapterNo != null) {
      setActiveChapterIndex(chapterNo)
      if (novelId) setChapterDrafting(novelId, chapterNo)
    }
    onNavigate?.()
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-scene-id="${sceneId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const focusChapter = (chapter: NovelActTreeType<NovelChapterSchema>) => {
    const no = chapter.autoNumber || 1
    setActiveChapterIndex(no)
    if (novelId) setChapterDrafting(novelId, no)
    const first = chapter.children?.[0]
    if (first) {
      focusScene(first.id, no)
      return
    }
    onNavigate?.()
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-chapter-id="${chapter.id}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  if (!tree.length) {
    return (
      <div className={cn('p-3 text-sm thesis-muted', className)}>
        暂无目录
      </div>
    )
  }

  return (
    <nav className={cn('thesis-outline h-full overflow-y-auto px-2 py-3', className)} aria-label="论文目录">
      <div className="px-2 mb-2 text-xs thesis-muted font-medium tracking-wide">目录</div>
      {tree.map((act) => (
        <div key={act.id} className="mb-1">
          {(act.children || []).map((chapter) => {
            const ch = chapter as NovelActTreeType<NovelChapterSchema>
            const chNo = ch.autoNumber || 0
            const active = activeChapterIndex === chNo
            const contract = MBA_CHAPTER_CONTRACTS.find((c) => c.chapterIndex === chNo)
            const title =
              shortChapterTitle(
                ch.data?.attributes?.title ||
                  contract?.title ||
                  ch.shortcutName ||
                  `第${chNo}章`,
              )
            const st = brief?.chapterGates[String(chNo)]?.status
            return (
              <div key={ch.id} className="mb-1">
                <button
                  type="button"
                  className={cn(
                    'w-full text-left rounded-md px-2 py-1.5 text-sm font-semibold transition-colors',
                    active ? 'bg-fill-2 text-primary' : 'hover:bg-fill-2',
                  )}
                  onClick={() => focusChapter(ch)}
                >
                  <span className="flex items-baseline justify-between gap-1">
                    <span className="truncate">
                      {chNo ? `${chNo}. ` : ''}
                      {title}
                    </span>
                    {st ? (
                      <span className="shrink-0 text-[11px] font-normal thesis-muted">
                        {gateStatusLabel(st)}
                      </span>
                    ) : null}
                  </span>
                </button>
                {/* 每章默认只有一节：点章即进正文，不再展开「正文草稿」占位 */}
                {(ch.children || []).length > 1 ? (
                  <ul className="ml-1.5 border-l border-[var(--tt-line,rgba(0,0,0,.08))] pl-1.5">
                    {(ch.children || []).map((scene, si) => {
                      const raw =
                        (scene.data?.attributes as { title?: string } | undefined)?.title?.trim() ||
                        ''
                      const stitle =
                        (!raw || raw === '正文草稿'
                          ? contract?.suggestedSections?.[si]
                          : raw) ||
                        scene.shortcutName ||
                        `小节 ${si + 1}`
                      return (
                        <li key={scene.id}>
                          <button
                            type="button"
                            className="w-full text-left rounded px-2 py-1 text-[13px] thesis-muted hover:bg-fill-2 hover:text-inherit truncate"
                            onClick={() => focusScene(scene.id, chNo || undefined)}
                          >
                            {stitle}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                ) : null}
              </div>
            )
          })}
        </div>
      ))}
    </nav>
  )
}
